// Per-deck grouped artifact library (target-PRD T1.1) — the rebuilt
// "click a built page → see everything grouped" panel. Version groups
// newest-first; legacy (pre-manifest) groups labeled instead of blank.
import { useEffect, useState } from 'react';
import { PanelState } from '../uikit';
import { ArrowBendUpLeft, Eye, FileCode, FileDoc, FileImage, FilePdf, FileText, Gear, Package } from '@phosphor-icons/react';
import type { ViewerTarget } from './ArtifactViewer';

export interface ArtifactGroupFile {
  filename: string;
  size: number;
  mime: string;
  slide_num: number | null;
  references: number;
  views: number;
  preview_url: string;
}
export interface ArtifactGroup {
  version: string;
  legacy: boolean;
  updated_at: number;
  files: ArtifactGroupFile[];
}

function MimeIcon({ mime }: { mime: string }) {
  const common = { size: 14, weight: 'light' as const, style: { flexShrink: 0, opacity: 0.75 } };
  if (mime === 'text/html') return <FileCode {...common} />;
  if (mime.startsWith('image/')) return <FileImage {...common} />;
  if (mime === 'application/json') return <Gear {...common} />;
  if (mime === 'text/markdown') return <FileText {...common} />;
  if (mime === 'application/pdf') return <FilePdf {...common} />;
  if (mime.startsWith('text/')) return <FileDoc {...common} />;
  return <Package {...common} />;
}

export default function ArtifactLibraryPanel({
  deckId,
  refreshKey = 0,
  onOpen,
  onRebuildLegacy,
}: {
  deckId: string;
  refreshKey?: number;
  onOpen: (t: ViewerTarget) => void;
  onRebuildLegacy?: (version: string) => void;
}) {
  const [groups, setGroups] = useState<ArtifactGroup[] | null>(null);
  const [current, setCurrent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setGroups(null); setError(null); setCurrent(null);
    fetch(`/api/decks/${deckId}/artifact-groups`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
      .then((d: { groups: ArtifactGroup[]; current_version: string | null }) => {
        if (!cancelled) { setGroups(d.groups); setCurrent(d.current_version); }
      })
      .catch(e => { if (!cancelled) setError(String(e?.message ?? e)); });
    return () => { cancelled = true; };
  }, [deckId, refreshKey, retryKey]);

  if (error) return <PanelState kind="error" title="Couldn't load files" description="Try reading the saved files for this project again." action="Retry files" onAction={() => setRetryKey(k => k + 1)} />;
  if (!groups) return <PanelState kind="loading" title="Loading files" description="Reading this project's generated files and versions." />;
  if (groups.length === 0) return <PanelState title="No files yet" description="Build your presentation from Storyboard, or ask the agent to create a file in Chat. The results will appear here." />;

  return (
    <div style={{ padding: '8px 12px', overflowY: 'auto', height: '100%' }}>
      {groups.map(g => (
        <div key={g.version} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0' }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{g.version}</span>
            {g.version === current && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: 'var(--ds-accent-soft)', color: 'var(--ds-accent)' }}>current</span>}
            {g.legacy && <span title="Built before the manifest contract — rebuild to make it renderable" style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: 'var(--ds-surface-subtle)', opacity: 0.7 }}>legacy — rebuild to view</span>}
            {g.legacy && onRebuildLegacy && (
              <button
                onClick={() => onRebuildLegacy(g.version)}
                style={{ fontSize: 10, padding: '1px 8px', borderRadius: 8, border: '1px solid var(--ds-overlay)', background: 'none', cursor: 'pointer' }}
                title="Extract this version's text and rebuild it under the current contract"
              >Rebuild</button>
            )}
          </div>
          {g.files.map(f => (
            <button
              type="button" className="pp-artifact-file" disabled={g.legacy}
              data-artifact-key={`${deckId}/${g.version}/${f.filename}`}
              key={f.filename}
              onClick={() => !g.legacy && onOpen({ deckId, version: g.version, filename: f.filename, mime: f.mime, previewUrl: f.preview_url })}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 8px', borderRadius: 6, minHeight:44, width:'100%', background:'transparent', border:0, color:'var(--ds-text)', textAlign:'left', fontFamily:'inherit',
                cursor: g.legacy ? 'default' : 'pointer', opacity: g.legacy ? 0.45 : 1, fontSize: 12,
              }}
              onMouseOver={e => { if (!g.legacy) e.currentTarget.style.background = 'var(--ds-overlay)'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'none'; }}
            >
              <MimeIcon mime={f.mime} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.filename}</span>
              {f.slide_num !== null && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, opacity: 0.55, flexShrink: 0 }} title="references · views">
                  <ArrowBendUpLeft size={11} weight="light" /> {f.references}
                  <span style={{ opacity: 0.5, margin: '0 2px' }}>·</span>
                  <Eye size={11} weight="light" /> {f.views}
                </span>
              )}
              <span style={{ opacity: 0.4, flexShrink: 0, minWidth: 48, textAlign: 'right' }}>{f.size >= 1024 ? `${(f.size / 1024).toFixed(1)}KB` : `${f.size}B`}</span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}
